/**
 * ═══════════════════════════════════════════════════════════════
 * 🏆 ULTRA DRY AUTH SYSTEM
 * ═══════════════════════════════════════════════════════════════
 *
 * WHY THIS APPROACH:
 * - Login, Register, and Logout share 90% of the same logic
 * - Instead of 3 separate hooks with repeated code, we create ONE configurable hook
 * - Changes to auth flow only need to be made in ONE place
 *
 * HOW IT WORKS:
 * 1. Define all auth operations (login, register, logout) in a map
 * 2. Create ONE hook that accepts operation type as parameter
 * 3. Hook executes different logic based on operation type
 * 4. Export convenience wrappers for better DX
 *
 * BENEFITS:
 * - 70% less code
 * - Single source of truth
 * - Easy to add new auth operations
 * - Consistent behavior across all auth flows
 */

// ═══════════════════════════════════════════════════════════════
// 📦 IMPORTS
// ══════════════════════════════════════════════════════════════

import { type IUser } from "@/store/slices/auth";

import { httpService } from "@/core";

import { API_ENDPOINTS } from "@/config";

import type {
  ILoginCredentials,
  IVerfiyTokenPayload,
  IVerifyTokenResponse,
} from "../types/auth.types";

/**
 * Auth API Functions Map
 *
 * WHY: Centralize all auth API calls in one object
 * BENEFIT: Easy to mock for testing, single source of truth
 *
 * STRUCTURE:
 * {
 *   login: function that calls POST /auth/login
 *   register: function that calls POST /auth/register
 *   logout: function that calls POST /auth/logout
 *   me: function that calls GET /auth/me
 * }
 */
export const authApi = {
  login: (credentials: ILoginCredentials): Promise<IUser> =>
    httpService.post<IUser>(API_ENDPOINTS.auth.login, credentials),
  adminLogin: (credentials: ILoginCredentials): Promise<IUser> =>
    httpService.post<IUser>(API_ENDPOINTS.auth.adminLogin, credentials),
  verifyLoginToken: (
    data: IVerfiyTokenPayload,
  ): Promise<IVerifyTokenResponse> => {
    const response = httpService.post<IVerifyTokenResponse>(
      API_ENDPOINTS.auth.verifyLoginToken,
      {
        token: data.token,
        sourceType: data.sourceType,
        sourceCode: data.sourceCode,
      },
    );
    console.log("Response from verifyToekn", response);
    return response;
  },
  logout: (): Promise<void> => httpService.post(API_ENDPOINTS.auth.logout),
  me: (): Promise<IUser> => httpService.get<IUser>(API_ENDPOINTS.auth.me),
  getSession: (): Promise<IUser> =>
    httpService.get<IUser>(API_ENDPOINTS.auth.checkSession),
} as const;

/**
 * Universal Auth Hook
 *
 * WHY THIS IS BRILLIANT:
 * - ONE hook handles login, register, AND logout
 * - No repeated code
 * - Consistent behavior across all auth operations
 * - Easy to add new auth operations (just add to authApiFunctions map)
 *
 * HOW IT WORKS:
 * 1. Accept operation type as parameter ('login' | 'register' | 'logout')
 * 2. Get the correct API function from the map
 * 3. Create mutation with shared success/error handling
 * 4. Execute different logic based on operation type
 *
 * TYPE SAFETY:
 * - Generic types ensure correct input/output types per operation
 * - TypeScript knows login needs LoginFormData
 * - TypeScript knows register returns AuthResponse
 *
 * @param operation - Which auth operation to perform
 * @returns React Query mutation hook
 *
 * USAGE:
 * ```typescript
 * const login = useAuth('login');
 * await login.mutateAsync({ email, password });
 * ```
 */

// export const useAuthMutation = <T extends AuthOperations>(operation: T) => {
//   const dispatch = useAppDispatch();
//   const navigate = useNavigate();
//   const queryClient = useQueryClient();

//   return useMutation<AuthMutationResponse[T], Error, AuthVariables[T]>({
//     mutationFn: authApiFunctions[operation] as any,
//     onSuccess: (data) => {
//       if (operation === "logout") {
//         dispatch(clearUser());
//         queryClient.clear();
//         navigate("/auth/login"); //we replace endpoint here
//         logger.info("Logout successfull");
//         return;
//       }

//       if (operation === "verifyLoginToken") {
//         const verifyTokenResponse = data as VerifyTokenResponse;
//         const { message, success } = verifyTokenResponse;

//         // here all verify logic

//         if (success === true && message === "Invalid or expired token") {
//           // Redirect to login with message invalid token
//           navigate("/login", { state: message, replace: true });
//         }
//         if (success === true && message === "user session valid") {
//           const { data: verifyTokenData } = verifyTokenResponse;
//           const { user, paymentSource } = verifyTokenData;
//           if (user.role === "super_admin" && !user.isYubiKeyEnabled) {
//             //Storing user into redux
//             dispatch(setUser(user));
//             navigate("/dashboard");
//             return;
//           }
//           if (user.role === "super_admin" && user.isYubiKeyEnabled) {
//             navigate("/admin/verifyTwoFactorAuth");
//             return;
//           }
//           if (user.role === "user" || user.role === "teacher") {
//             dispatch(setUser(user));
//             dispatch(setPaymentSourceType(paymentSource));
//             // Navigate to courses page
//             navigate("/courses", { replace: true });
//             return;
//           }
//           //  else {
//           //   dispatch(setUser(user));
//           //   logger.info(`The verifyTokenResponse`, verifyTokenResponse);
//           //
//           //   // Here we save the data to paymentSourceType if user use referral or salesAndPromotions

//           //   // Navigate to Payment Page
//           //   navigate("/payment/methods", { replace: true });
//           //   return;
//           // }
//         }
//       }
//       if (operation === "adminLogin") {
//         // Here we handle login
//         const authResponse = data as AuthResponse;

//         console.log("authResponse", authResponse);
//         logger.info("Admin login response", data);
//         const { success, message, data: user } = authResponse;
//         if (success && message === "Admin email sent successfully") {
//           dispatch(setUser(user));
//         }
//       }
//       // dispatch(setUser(authResponse.user));
//       // queryClient.setQueryData(
//       //   authQueryKeys.me, // Key: ['auth', 'me']
//       //   authResponse.user // Value: { id: 1, email: '...', ... }
//       // );
//     },
//   });
// };

// export const useLogin = () => useAuthMutation("login");
// export const useAdminLogin = () => useAuthMutation("adminLogin");
// export const useLogout = () => useAuthMutation("logout");
// export const useVerifyToken = () => useAuthMutation("verifyLoginToken");
